import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Form, Field } from 'react-final-form';
import createDecorator from 'final-form-focus';
import ReCAPTCHA from 'react-google-recaptcha';

import styled, { css } from 'styled-components';
import Button from '@joinforma/ui/components/Button';
import Body2 from '@joinforma/ui/components/Body2';
import TextField from '@joinforma/ui/components/TextField';
import Title1 from '@joinforma/ui/components/Title1';
import {
  TEXT_FIELD_TYPES,
  AUTOCOMPLETE_TYPES,
  BUTTON_VARIANTS,
  BUTTON_SIZES,
  BUTTON_TYPES,
} from '@joinforma/ui/constants/properties';
import { SCREEN_WIDTHS } from '@joinforma/ui/constants/breakpoints';

import { getImage } from 'utils/cloudinary';

import { RootRoutes } from 'utils/routeGenerator';
import { validateRegex, validateRequired } from 'utils/validate';

interface Props {
  status?: string;
  email?: string;
  type: string | string[] | null;
  onNext: ({
    email,
    password,
    recaptcha,
  }: {
    email?: string;
    password: string;
    recaptcha?: string | null;
  }) => void;
}

const Container = styled.div`
  background: ${({
    theme: {
      colors: {
        background: { primary },
      },
    },
  }) => primary};
  padding: 56px;
  display: flex;
  flex-direction: column;
  width: 600px;
  box-shadow: 1px 1px 8px rgb(0 0 0 / 20%);
  border-radius: 5px;
  margin: 0 20px;
  @media (min-width: ${SCREEN_WIDTHS.MOBILE_LANDSCAPE}) {
    margin: 0;
  }
`;
const Title = styled(Title1)`
  margin-bottom: 8px;
`;
const Subtitle = styled(Body2)`
  color: ${({
    theme: {
      colors: {
        text: { secondary },
      },
    },
  }) => secondary};
`;

const PasswordInstruction = styled(Body2)`
  color: ${({
    theme: {
      colors: {
        text: { secondary },
      },
    },
  }) => secondary};
  margin-bottom: 8px;
`;

const welcomeButtonStyles = css`
  width: 100%;
`;

const SigninButton = styled(Button).attrs({
  variant: BUTTON_VARIANTS.PRIMARY,
  size: BUTTON_SIZES.LARGE,
  type: BUTTON_TYPES.SUBMIT,
})`
  ${welcomeButtonStyles}
  margin-top: 32px;
`;

const MagicLinkButton = styled(Button).attrs({
  variant: BUTTON_VARIANTS.SECONDARY,
  size: BUTTON_SIZES.LARGE,
})`
  ${welcomeButtonStyles}
`;

const NextButton = styled(Button).attrs({
  variant: BUTTON_VARIANTS.PRIMARY,
  size: BUTTON_SIZES.LARGE,
})`
  width: 288px;
  margin-bottom: 10px;
`;

const InfoSentText = styled(Title1)`
  margin-bottom: 10px;
`;

const Separator = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  margin: 16px auto;
  width: 100%;
  color: #9e9ea6;

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid #ddd;
  }

  &:not(:empty)::before {
    margin-right: 0.25em;
  }

  &:not(:empty)::after {
    margin-left: 0.25em;
  }
`;

const StyledTextField = styled(TextField)``;

const StyledLink = styled(Link)`
  width: 100%;
  color: ${({
    theme: {
      colors: {
        text: { link },
      },
    },
  }) => link};

  &:hover {
    color: ${({
      theme: {
        colors: {
          text: { link_hover },
        },
      },
    }) => link_hover};
  }
`;

const MagicLinkImage = styled.img`
  height: 50px;
  margin-bottom: 40px;
  object-fit: contain;
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin: 0 auto;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  margin-bottom: 40px;
  align-items: center;
`;

const StyledReCAPTCHA = styled(ReCAPTCHA)`
  margin-bottom: 24px;
`;

const focusOnErrors = createDecorator();

const required = (value: string) =>
  value ? undefined : 'This field cannot be blank';

export const isLoginEmailValid = (value = '') =>
  validateRequired({
    input: value,
    rule: true,
    err_msg: 'Email is required',
  }) ||
  validateRegex({
    input: value,
    rule: /\S+@\S+\.\S+/,
    err_msg: 'Enter a valid email address',
  });

const LoginChooser = () => (
  <>
    <Title>Sign in to your account</Title>
    <Subtitle>Use your company's SSO or password to sign in.</Subtitle>
    <SigninButton to={RootRoutes.login({ type: 'sso' })}>Sign in</SigninButton>
    <Separator>OR</Separator>
    <MagicLinkButton to={RootRoutes.login({ type: 'magic' })}>
      Get Magic Link
    </MagicLinkButton>
  </>
);

const EmailInput = ({
  onNext,
  loading,
}: {
  onNext: ({
    email,
    password,
    recaptcha,
  }: {
    email?: string;
    password: string;
    recaptcha?: string | null;
  }) => void;
  loading: boolean;
}) => (
  <Form
    onSubmit={onNext}
    decorators={[focusOnErrors]}
    render={({ handleSubmit }) => (
      <FormContainer onSubmit={handleSubmit}>
        <Title>Log in</Title>
        <Field
          name="email"
          validate={isLoginEmailValid}
          render={({ input, meta }) => (
            <InputWrapper>
              <StyledTextField
                {...input}
                label="Email address"
                autocomplete={AUTOCOMPLETE_TYPES.EMAIL}
                error={meta.touched && meta.error}
              />
            </InputWrapper>
          )}
        />

        <NextButton loading={loading} onClick={handleSubmit}>
          Next
        </NextButton>
      </FormContainer>
    )}
  />
);

const MagicLinkSent = ({ email }: { email?: string }) => (
  <>
    <MagicLinkImage
      src={getImage('public/magiclink_pmbuci.png', { height: 50 })}
      alt={'Magic Link'}
    />
    <InfoSentText>Your Magic Link is on its way!</InfoSentText>
    <Subtitle>
      To log in, tap the button in the email we sent to {email}
    </Subtitle>
    <Separator />
    <Subtitle>
      Have a password?{' '}
      <StyledLink to={RootRoutes.login({ type: 'password' })}>
        Log in here
      </StyledLink>
    </Subtitle>
  </>
);

const PasswordLogin = ({
  email,
  onNext,
  loading,
  captchaValue,
  setCaptchaValue,
}: {
  email?: string;
  onNext: ({
    email,
    password,
    recaptcha,
  }: {
    email?: string;
    password: string;
    recaptcha?: string | null;
  }) => void;
  loading: boolean;
  captchaValue?: string | null;
  setCaptchaValue: (token: string | null) => void;
}) => {
  const { REACT_APP_RECAPTCHA_KEY } = process.env;

  return (
    <Form
      onSubmit={(data: {
        email?: string;
        password: string;
        recaptcha?: string | null;
      }) => {
        data.recaptcha = captchaValue;
        data.email = email;
        onNext(data);
      }}
      decorators={[focusOnErrors]}
      render={({ handleSubmit }) => (
        <FormContainer onSubmit={handleSubmit}>
          <Title>Log in</Title>
          <PasswordInstruction>Enter your password</PasswordInstruction>
          <Field
            name="password"
            type="password"
            validate={required}
            render={({ input, meta }) => (
              <InputWrapper>
                <StyledTextField
                  {...input}
                  label="Password"
                  type={TEXT_FIELD_TYPES.PASSWORD}
                  autocomplete={AUTOCOMPLETE_TYPES.CURRENT_PASSWORD}
                  error={meta.touched && meta.error}
                />
              </InputWrapper>
            )}
          />

          {REACT_APP_RECAPTCHA_KEY && (
            <StyledReCAPTCHA
              sitekey={REACT_APP_RECAPTCHA_KEY}
              onChange={setCaptchaValue}
            />
          )}

          <NextButton loading={loading} onClick={handleSubmit}>
            Login
          </NextButton>
          <Subtitle>
            Forgot your password?{' '}
            <StyledLink to={RootRoutes.loginPasswordReset()}>
              Reset here
            </StyledLink>
          </Subtitle>
        </FormContainer>
      )}
    />
  );
};

const PasswordResetSent = ({ email }: { email?: string }) => (
  <>
    <MagicLinkImage
      src={getImage('public/magiclink_pmbuci.png', { height: 50 })}
      alt={'Magic Link'}
    />
    <InfoSentText>Your reset email is on its way!</InfoSentText>
    <Subtitle>
      To reset your account, tap the button in the email we sent to {email}
    </Subtitle>
    <Separator />
  </>
);

const Login = ({ status, email, type, onNext }: Props) => {
  const [captchaValue, setCaptchaValue] = useState<string | null>();

  if (type === 'password' && !email) {
    return <Navigate replace to="/login" />;
  }

  return (
    <Container>
      {!type && <LoginChooser />}

      {type === 'magic' &&
        (status === 'sent' ? (
          <MagicLinkSent email={email} />
        ) : (
          <EmailInput onNext={onNext} loading={status === 'loading'} />
        ))}

      {type === 'sso' && (
        <EmailInput onNext={onNext} loading={status === 'loading'} />
      )}

      {type === 'password' && (
        <PasswordLogin
          onNext={onNext}
          email={email}
          loading={status === 'loading'}
          captchaValue={captchaValue}
          setCaptchaValue={setCaptchaValue}
        />
      )}

      {type === 'password-reset' && <PasswordResetSent email={email} />}
    </Container>
  );
};

export default Login;
